import { positionChecker } from 'src/util/useModelDesign'
import { I18NText, Position, State, StateObj } from 'src/types'

const genGloveColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
  head,
  label: stateObj?.label,
  color: stateObj?.color,
  value: stateObj?.value,
  partsKey
})

// 基本項目ロジック
export const getGorillaBaseCells = (state: State, position: Position) => {
  const genGloveBaseCell = (head: string, stateObj: StateObj) => ({ head, label: stateObj?.label, value: stateObj?.value })
  const { isPitcher, isMitt } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const baseModelStateObj = {
    label: productNumber,
    value: productNumber
  }
  const isBomberSeries = isPitcher && state.series?.value === 'bomber'
  return [
    genGloveBaseCell('基本モデル', baseModelStateObj),
    isPitcher && genGloveBaseCell('シリーズ', state?.series),
    genGloveBaseCell('利き腕', state.dominantArm),
    isBomberSeries && genGloveBaseCell('サイズ', state.size),
    !isBomberSeries && genGloveBaseCell('指カバー/指当て', state.fingerGuardType),
    !isBomberSeries && !isMitt && genGloveBaseCell('ウェブパーツ', state.webParts),
    !isMitt && genGloveBaseCell('背面紐通し', state.backLace)
  ].filter(Boolean)
}

export const checkGorillaWeb2 = (webPartsValue: string): boolean => {
  return ['w26'].includes(webPartsValue)
}

// グラブ / カラー項目
export const getGorillaGloveColorCells = (state: State, t: I18NText) => {
  const needFingerCoverOrPad = state.fingerGuardType?.value !== 'none'
  const needWeb2 = checkGorillaWeb2(state?.webParts?.value)
  const productNumber = state.baseModel.productNumber
  const is001Model = ['001'].includes(productNumber) // 投手
  const isHudModel = ['HUD'].includes(productNumber) // 投手
  const isBknModel = ['BKN'].includes(productNumber) // 外野手
  return [
    genGloveColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genGloveColorCell(t.WEB, state.web, 'web'),
    needWeb2 && genGloveColorCell(t.WEB2, state.web2, 'web2'),
    genGloveColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genGloveColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genGloveColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genGloveColorCell(t.LININGS, state.linings, 'linings'),
    genGloveColorCell(t.THUMB1, state.thumbOut, 'thumbOut'),
    genGloveColorCell(t.THUMB2, state.thumbWeb, 'thumbWeb'),
    genGloveColorCell(t.INDEX + '1', state.indexWeb, 'indexWeb'),
    genGloveColorCell(t.INDEX + '2', state.indexMiddle, 'indexMiddle'),
    genGloveColorCell(t.MIDDLE + '1', state.middleIndex, 'middleIndex'),
    genGloveColorCell(t.MIDDLE + '2', state.middleRing, 'middleRing'),
    genGloveColorCell(t.RING + '1', state.ringMiddle, 'ringMiddle'),
    genGloveColorCell(t.RING + '2', state.ringLittle, 'ringLittle'),
    genGloveColorCell(t.LITTLE + '1', state.littleRing, 'littleRing'),
    genGloveColorCell(t.LITTLE + '2', state.littleOut, 'littleOut'),
    !is001Model && genGloveColorCell(t.BAND, state.listBelt, 'listBelt'),
    isHudModel && genGloveColorCell('HUD1', state?.littleOut, 'littleOut'), // 小指パーツとして扱う
    isHudModel && genGloveColorCell('HUD2', state?.littleRing, 'littleRing'), // 同上
    isBknModel && genGloveColorCell('BKN1', state?.littleOut, 'littleOut'),
    isBknModel && genGloveColorCell('BKN2', state?.littleRing, 'littleRing'),
    genGloveColorCell(t.LACE, state.lace, 'lace'),
    genGloveColorCell(t.EDGE, state.edge, 'edge'),
    genGloveColorCell(t.STITCH, state.stitch, 'stitch'),
    genGloveColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    genGloveColorCell('ムートン', state.mouton, 'mouton'),
    genGloveColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ].filter(Boolean)
}

// ファーストミット / カラー項目
export const getGorillaFirstMittColorCells = (state: State, t: I18NText) => {
  const genFirstMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color,
    value: stateObj?.value,
    partsKey
  })
  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  return [
    genFirstMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genFirstMittColorCell(t.WEB, state.web, 'web'),
    genFirstMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genFirstMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genFirstMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genFirstMittColorCell(t.LININGS, state.linings, 'linings'),
    genFirstMittColorCell(t.THUMB, state.thumb, 'thumb'),
    genFirstMittColorCell(t.UNDER_WEB, state.underWeb, 'underWeb'),
    genFirstMittColorCell(t.BOOMERANG, state.boomerang, 'boomerang'),
    genFirstMittColorCell(t.BAND, state.listBelt, 'listBelt'),
    genFirstMittColorCell(t.LACE, state.lace, 'lace'),
    genFirstMittColorCell(t.EDGE, state.edge, 'edge'),
    genFirstMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genFirstMittColorCell('ムートン', state.mouton, 'mouton'),
    genFirstMittColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ]
}

// キャッチャーミット / カラー項目
export const getGorillaCatcherMittColorCells = (state: State, t: I18NText) => {
  const genCatcherMittColorCell = (head: string, stateObj: StateObj, partsKey: string) => ({
    head,
    label: stateObj?.label,
    color: stateObj?.color || 'No data',
    value: stateObj?.value,
    partsKey
  })

  const needFingerCoverOrPad = state.fingerGuardType.value !== 'none'
  return [
    genCatcherMittColorCell(t.CATCH_SURFACE, state.catchFace, 'catchFace'),
    genCatcherMittColorCell(t.WEB, state.web, 'web'),
    genCatcherMittColorCell(t.THUMB_HOOK, state.thumbHook, 'thumbHook'),
    genCatcherMittColorCell(t.LITTLE_HOOK, state.littleHook, 'littleHook'),
    needFingerCoverOrPad && genCatcherMittColorCell(t.FINGER_GUARD, state.indexCover, 'indexCover'),
    genCatcherMittColorCell(t.LININGS, state.linings, 'linings'),
    genCatcherMittColorCell(t.STAND, state.fingerStand, 'fingerStand'),
    genCatcherMittColorCell('親指・人差指・中指1', state.thumbIndexMiddleRight, 'thumbIndexMiddleRight'),
    genCatcherMittColorCell('中指2・薬指・小指', state.middleLeftRingLittle, 'middleLeftRingLittle'),
    genCatcherMittColorCell(t.EDGE, state.edge, 'edge'),
    genCatcherMittColorCell(t.LACE, state.lace, 'lace'),
    genCatcherMittColorCell(t.STITCH, state.stitch, 'stitch'),
    genCatcherMittColorCell(t.HAMIDASHI, state.hamidashi, 'hamidashi'),
    genCatcherMittColorCell('ムートン', state.mouton, 'mouton'),
    genCatcherMittColorCell('ごりら印ラベル', state?.gorillaLabel, 'gorillaLabel')
  ].filter(Boolean)
}
